/**
 * Tiny runtime validators for untrusted payloads (every client message is validated before any
 * handler sees it). Validators throw `SchemaError`; `Infer<typeof schema>` gives the static type.
 *
 *   const MoveSchema = s.obj({ x: s.num({ min: -1e4, max: 1e4 }), v: s.opt(s.int({ min: 0 })) });
 *   type Move = Infer<typeof MoveSchema>;
 */

export class SchemaError extends Error {
  constructor(
    readonly path: string,
    message: string,
  ) {
    super(`${path || 'payload'}: ${message}`);
    this.name = 'SchemaError';
  }
}

export type Validator<T> = (value: unknown, path?: string) => T;
export type Infer<V> = V extends Validator<infer T> ? T : never;

interface NumOpts {
  min?: number;
  max?: number;
}

function num(opts: NumOpts = {}): Validator<number> {
  return (v, path = '') => {
    if (typeof v !== 'number' || !Number.isFinite(v)) throw new SchemaError(path, 'expected a finite number');
    if (opts.min !== undefined && v < opts.min) throw new SchemaError(path, `must be >= ${opts.min}`);
    if (opts.max !== undefined && v > opts.max) throw new SchemaError(path, `must be <= ${opts.max}`);
    return v;
  };
}

function int(opts: NumOpts = {}): Validator<number> {
  const n = num(opts);
  return (v, path = '') => {
    const x = n(v, path);
    if (!Number.isSafeInteger(x)) throw new SchemaError(path, 'expected an integer');
    return x;
  };
}

function str(opts: { min?: number; max: number; pattern?: RegExp }): Validator<string> {
  return (v, path = '') => {
    if (typeof v !== 'string') throw new SchemaError(path, 'expected a string');
    if (v.length < (opts.min ?? 0)) throw new SchemaError(path, `must be at least ${opts.min} characters`);
    if (v.length > opts.max) throw new SchemaError(path, `must be at most ${opts.max} characters`);
    if (opts.pattern && !opts.pattern.test(v)) throw new SchemaError(path, 'has an invalid format');
    return v;
  };
}

function bool(): Validator<boolean> {
  return (v, path = '') => {
    if (typeof v !== 'boolean') throw new SchemaError(path, 'expected a boolean');
    return v;
  };
}

function lit<const T extends readonly (string | number)[]>(...values: T): Validator<T[number]> {
  return (v, path = '') => {
    if (!values.includes(v as T[number])) throw new SchemaError(path, `expected one of ${values.join(', ')}`);
    return v as T[number];
  };
}

function opt<T>(inner: Validator<T>): Validator<T | undefined> {
  return (v, path = '') => (v === undefined || v === null ? undefined : inner(v, path));
}

function arr<T>(item: Validator<T>, opts: { max: number }): Validator<T[]> {
  return (v, path = '') => {
    if (!Array.isArray(v)) throw new SchemaError(path, 'expected an array');
    if (v.length > opts.max) throw new SchemaError(path, `at most ${opts.max} entries`);
    return v.map((x, i) => item(x, `${path}[${i}]`));
  };
}

type Shape = Record<string, Validator<unknown>>;
type ObjOf<S extends Shape> = { [K in keyof S as undefined extends Infer<S[K]> ? never : K]: Infer<S[K]> } & {
  [K in keyof S as undefined extends Infer<S[K]> ? K : never]?: Infer<S[K]>;
};
type Flatten<T> = { [K in keyof T]: T[K] };

/** Plain object with exactly the listed keys. Unknown keys are dropped (never forwarded to handlers). */
function obj<S extends Shape>(shape: S): Validator<Flatten<ObjOf<S>>> {
  return (v, path = '') => {
    if (typeof v !== 'object' || v === null || Array.isArray(v)) throw new SchemaError(path, 'expected an object');
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(shape)) {
      const val = shape[key]!(src[key], path ? `${path}.${key}` : key);
      if (val !== undefined) out[key] = val;
    }
    return out as Flatten<ObjOf<S>>;
  };
}

/** Accept anything (for messages with no payload). Returns an empty object. */
function empty(): Validator<Record<string, never>> {
  return () => ({});
}

export const s = { num, int, str, bool, lit, opt, arr, obj, empty };
